// src/utils/helpers.js

/**
 * Sorts an array of objects by key and direction
 * @param {object[]} array 
 * @param {string} key 
 * @param {'asc' | 'desc'} direction `asc` or `desc`
 * @returns {object[]} sorted object
 */
export function sortArrayByKey(array, key, direction = 'asc') {
  const modifier = direction === 'desc' ? -1 : 1;

  return [...array].sort((a, b) => {
    let valA = a[key];
    let valB = b[key];

    // Handle null or undefined values gracefully by pushing them to the bottom
    if (valA === null || valA === undefined) return 1;
    if (valB === null || valB === undefined) return -1;

    // 1. String Sort (Case-insensitive alphabetical handling)
    if (typeof valA === 'string' && typeof valB === 'string') {
      return valA.localeCompare(valB, undefined, { sensitivity: 'base' }) * modifier;
    }

    // 2. Numeric or Boolean Sort
    if (valA < valB) return -1 * modifier;
    if (valA > valB) return 1 * modifier;
    return 0;
  });
}

/**
 * Converts all object keys to camelCase. Also works for array of objects.
 * @param {*} obj Object or Array<Object> to camelize
 * @returns 
 */
export function camelizeKeys (obj) {
  if (Array.isArray(obj)) return obj.map(v => camelizeKeys(v));
  if (obj !== null && obj.constructor === Object) {
    return Object.keys(obj).reduce((result, key) => {
      const camelKey = key.replace(/([-_][a-z])/gi, ($1) => 
        $1.toUpperCase().replace('-', '').replace('_', '')
      );
      result[camelKey] = camelizeKeys(obj[key]);
      return result;
    }, {});
  }
  return obj;
}

/**
 * Validate an ISO string format yyyy-mm-ddTHH:MM:SS[.SSS]Z
 * @param {*} str 
 * @returns {boolean}
 */
export function isIsoDate(str) {
  if (typeof str !== 'string') return false;
  // 1. Structural check: YYYY-MM-DD followed by optional time (ms optional)
  const isoRegex = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d{3})?Z)?$/;
  if (typeof str !== 'string' || !isoRegex.test(str)) return false;

  // 2. Validity check
  const d = new Date(str);
  if (isNaN(d.getTime())) return false;

  // 3. String match check (Handles edge cases like Feb 31)
  // Note: toISOString() ALWAYS includes milliseconds.
  // If the input lacks ms, compare it to a version with ms removed.
  const iso = d.toISOString();
  return iso === str || iso.replace(/\.\d{3}/, '') === str;
}

/**
 * Convert a value to its boolean representation
 * @param {*} val 
 * @returns {boolean}
 */
export function toBool(val) {
  if (typeof val === 'boolean') return val;
  if (typeof val === 'string') {
    if (val.toLowerCase() === 'true' || val === '1') return true;
    if (val.toLowerCase() === 'false' || val === '0') return false;
  }
  if (typeof val === 'number') {
    if (val >= 1) return true;
    if (val <= 0) return false;
  }
  return false;
}
